import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FileInfo, ValidImageExtensions } from 'src/app-client/_appCommon/base/AppInterfaceAndBaseClasses';
import { FormGroup } from '@angular/forms';
import { Alert, AlertType } from '../../../app/_appCommon/base/Alert';
import { Router } from '@angular/router';

@Component({
  selector: 'app-drag-and-drop',
  templateUrl: './drag-and-drop.component.html',
  styleUrls: ['./drag-and-drop.component.css']
})
export class DragAndDropComponent implements OnInit, OnDestroy
{
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
  @Input() textWElipsis: boolean = false;
  @Input() fileTitle: string;
  @Input() downloadFilePreview: boolean = true;
  @Input() isGenericFileType: boolean = false;
  @Input() accept: string;
  @Input() text: string;
  @Input() isRequired: boolean = false;
  @Input() parentForm: FormGroup;
  @Input() nameControl;
  @Input() pdfOnly: boolean = false;
  @Input() stlOnly: boolean = false;
  @Input() stlZipOnly: boolean = false;
  @Input() isReadOnly: boolean = false;
  @Output()
  public onSelectFile: EventEmitter<File> = new EventEmitter<File>();
  @Output()
  public onDeleteFile: EventEmitter<boolean> = new EventEmitter<boolean>();

  private file: File;

  public isFileSelected = false;

  public isLoading = true;

  public form: FormGroup;

  ngOnInit(): void { }

  constructor(private router: Router, private sanitizer: DomSanitizer, private ngZone: NgZone) { }

  /** Dirección interna del PDF ya traído a memoria. Null = no hay preview. */
  public pdfPreviewUrl: SafeResourceUrl = null;
  private pdfObjectUrl: string = null;

  /**
   * Trae el PDF y arma una dirección interna para mostrarlo.
   *
   * Apuntar el visor directamente a la dirección del archivo hace que el
   * navegador lo DESCARGUE en lugar de mostrarlo, porque el almacenamiento lo
   * entrega marcado como descarga. Trayéndolo primero, esa marca ya no aplica.
   * Si el almacenamiento no deja leerlo, falla limpiamente y queda el ícono.
   */
  private loadPdfPreview(url: string): void
  {
    this.releasePdfPreview();
    if (!url) { return; }

    fetch(url)
      .then(response => response.ok ? response.blob() : Promise.reject(response.status))
      .then(blob =>
      {
        if (blob.type && blob.type.indexOf('pdf') === -1) { return Promise.reject('no es pdf'); }
        this.ngZone.run(() =>
        {
          this.pdfObjectUrl = URL.createObjectURL(blob);
          this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfObjectUrl + '#view=FitH&toolbar=0');
        });
      })
      .catch(() => this.ngZone.run(() => { this.pdfPreviewUrl = null; }));
  }

  private releasePdfPreview(): void
  {
    if (this.pdfObjectUrl) { URL.revokeObjectURL(this.pdfObjectUrl); this.pdfObjectUrl = null; }
    this.pdfPreviewUrl = null;
  }

  ngOnDestroy(): void
  {
    this.releasePdfPreview();
  }

  private _fileSelected: FileInfo;

  @Input() set fileSelected(fileSelected: FileInfo | null)
  {
    const urlChanged = this._fileSelected?.url !== fileSelected?.url;
    this._fileSelected = fileSelected;

    if (urlChanged)
    {
      if (fileSelected?.url && this.isPdfFile) { this.loadPdfPreview(fileSelected.url.toString()); }
      else { this.releasePdfPreview(); }
    }

    if (fileSelected != null)
      this.parentForm.controls[this.nameControl].setValue(fileSelected.name);

    if (fileSelected)
    {
      if (!fileSelected.name && !fileSelected.url)
      {
        this.isFileSelected = false;
        this._fileSelected = null;
        this.fileInput.nativeElement.value = null;
      }
    }
  }

  get fileSelected(): FileInfo
  {
    return this._fileSelected;
  }

  public showConfirmDialog = false;

  public deleteFile()
  {
    this.showConfirmDialog = true;
  }

  public confirmDeleteFn = () =>
  {
    this.showConfirmDialog = false;
    this.isFileSelected = false;
    this.parentForm.controls[this.nameControl].reset();
    this.fileInput.nativeElement.value = null;
    this.onDeleteFile.emit(true);
  }

  public cancelDeleteFn = () =>
  {
    this.showConfirmDialog = false;
  }

  public changeFileListener(event): void
  {
    let file = event.srcElement.files[0];
    if (this.isValidFile(file))
      this.uploadFiles(file);
  }

  public onDropFile(file: File): void
  {
    if (this.isValidFile(file))
      this.uploadFiles(file);
  }

  public isValidFile(file: File): boolean
  {
    let extension = file.name.split('.').pop();

    if (file.size > 524288000)
    {
      this.deleteFile();
      new Alert(AlertType.Warning, "No se aceptan archivos mayores a 500MB");
    }
    else if (this.stlOnly && extension != "stl")
    {
      new Alert(AlertType.Warning, "Solo se aceptan archivos STL");
    }
    else if (this.stlZipOnly && !(extension == "stl" || extension == "zip"))
    {
      new Alert(AlertType.Warning, "Solo se aceptan archivos STL y ZIP");
    }
    else if (this.pdfOnly && file.type != "application/pdf")
    {
      new Alert(AlertType.Warning, "Solo se aceptan archivos PDF");
    }
    else
    {
      return true;
    }
    this.fileInput.nativeElement.value = null;
    return false;
  }

  public uploadFiles(file: File): void
  {
    this.fileInput.nativeElement.value = '';

    this.file = file;
    this.isFileSelected = true;
    this.onSelectFile.emit(this.file);
  }

  public openFileBrowser(): void
  {
    this.fileInput.nativeElement.click();
  }

  public openFullScreen(): void
  {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/stl-viewer'], { queryParams: { url: this.fileSelected.url.toString() } })
    );
    window.open(url, '_blank', 'noreferrer');
  }

  public downloadFile(): void
  {
    var url = this.fileSelected.url.toString();
    var s = url.split('.');
    var ext = '.' + s[s.length - 1];

    if (ValidImageExtensions.includes(ext))
    {
      url = this.router.serializeUrl(
        this.router.createUrlTree(['/img'], { queryParams: { path: this.fileSelected.url.toString() } })
      );
    }

    window.open(url, '_blank', 'noreferrer');
  }

  public get isPdfFile(): boolean {
    const url = this.fileSelected?.url?.toString() ?? '';
    const name = this.fileSelected?.name ?? '';
    return url.toLowerCase().includes('.pdf') || name.toLowerCase().includes('.pdf');
  }

  public get isZipFile(): boolean {
    const url = this.fileSelected?.url?.toString() ?? '';
    const name = this.fileSelected?.name ?? '';
    return url.toLowerCase().includes('.zip') || name.toLowerCase().includes('.zip');
  }

  /**
   * ¿El archivo es una imagen? Se decide por el archivo mismo. Si el nombre no
   * lo dice —puede venir sin extensión—, se respeta lo que declaró la pantalla:
   * una que no pide archivos genéricos espera imágenes.
   */
  public get isImageFile(): boolean {
    const url = this.fileSelected?.url?.toString()?.toLowerCase() ?? '';
    const name = this.fileSelected?.name?.toLowerCase() ?? '';
    // Los valores de la lista YA traen el punto ('.png'), así que no se le
    // antepone otro. Se agrega '.jpeg' porque la lista compartida lo mapea a
    // '.jpg' y un archivo llamado .jpeg no coincidiría.
    const sufijos = ValidImageExtensions.map(ext => ext.startsWith('.') ? ext : '.' + ext).concat('.jpeg');
    const esImagenPorNombre = sufijos.some(sufijo => url.includes(sufijo) || name.includes(sufijo));

    if (esImagenPorNombre) { return true; }

    return !this.isGenericFileType && !this.isPdfFile && !this.isZipFile && !this.isStlFile;
  }

  public get isStlFile(): boolean {
    const url = this.fileSelected?.url?.toString() ?? '';
    const name = this.fileSelected?.name ?? '';
    return url.toLowerCase().includes('.stl') || name.toLowerCase().includes('.stl');
  }

  public getFileNameFromUrl(fileUrl: string, fileName: string): string
  {
    let urlSplits = fileUrl.toString().split('/');
    let res = fileName;
    if(urlSplits.length > 0)
      res = urlSplits[urlSplits.length - 1]

    if(res.length > 30)
      res = this.shortenString(res);
    return res;
  }

  private shortenString(input: string): string {
    let processed = input;

    const prefix = input.slice(0, 18);
    if (/^\d{17}_$/.test(prefix)) {
      processed = input.slice(18);
    }

    const length = processed.length;

    if (length <= 30) {
      return processed;
    }

    const firstPart = processed.slice(0, 10);
    const lastPart = processed.slice(-20);

    return `${firstPart}...${lastPart}`;
  }
}
